import { Delete, FlipCameraAndroid, MoreVert } from "@mui/icons-material";
import {
    Box,
    css,
    IconButton,
    IconButtonProps,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    useTheme,
} from "@mui/material";
import { View } from "@novorender/api";
import { Toast } from "@procore/core-react";
import { quat, vec2, vec3 } from "gl-matrix";
import {
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { getClippingPlaneAnchorId, useClippingPlaneActions } from "features/clippingPlanes/useClippingPlaneActions";
import { getCameraDir } from "features/engine2D/utils";
import {
    CameraType,
    Picker,
    renderActions,
    RenderState,
    selectClippingPlanes,
    selectPicker,
    selectSectionPlaneLimitReached,
} from "features/render";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { clampToSafeArea, useViewportSafeArea, type ViewportSafeArea } from "hooks/useViewportSafeArea";
import { hasMouseSupport } from "utils/misc";

import { useSectionPlaneDrag } from "./hooks/useSectionPlaneDrag";
import { SECTION_PLANE_ARROW_BACK, SECTION_PLANE_DARK_GREEN, SECTION_PLANE_NEON } from "./sectionPlaneColors";
import {
    clearSectionPlaneCreateOrigin,
    showSectionPlaneMenuButtonAtPoint,
    useSectionPlaneCreateOrigin,
} from "./sectionPlaneCreateOrigin";
import { useSectionPlaneUndoRedo } from "./sectionPlaneUndoRedo";
import {
    computeSectionPlaneGizmoTarget,
    distributeAlongAxis,
    getActiveSectionPlaneIndex,
    getSectionPlaneArrowAngle,
    getSectionPlanePoints,
    getSectionPlaneViewCenterAnchor,
} from "./sectionPlaneUtils";

const dummyPoint = vec2.fromValues(-100, -100);

const POSITION_CHANGE_EPSILON = 0.5;
const MENU_VISIBILITY_SQR_DIST = 52 * 52;

// ── Section-plane gizmo geometry & interaction tuning ──────────────────────
const GIZMO_CENTER_X = 61.174;
const GIZMO_CENTER_Y = 60.359;
const GIZMO_RING_RADIUS = 55;
const GIZMO_HUB_RADIUS = 15;
const GIZMO_NATIVE_ARROW_ANGLE = 16;
const GIZMO_ROTATION_OFFSET = 90 - GIZMO_NATIVE_ARROW_ANGLE;
const GIZMO_MIN_SQUASH = 0.05;
// Gizmo center offset from the anchor point in canvas px.
const GIZMO_CANVAS_OFFSET_X = 15.587;
const GIZMO_CANVAS_OFFSET_Y = 14.18;
// Hover/drag + edge-clamp feedback.
const GIZMO_HOVER_RADIUS_PX = 34;
const GIZMO_HOVER_RING_SCALE = 1.22;
const GIZMO_RING_LERP = 0.18; // ring hover-scale easing per frame.
const GIZMO_EDGE_SCALE = 0.5; // overall gizmo size when clamped to the viewport edge.
const GIZMO_SIZE_LERP = 0.1; // edge-shrink easing per frame.
const GIZMO_DISPLAY_POS_LERP = 0.1; // safe-area display-position easing per frame.
const GIZMO_SPACING_PX = 70; // min px between gizmo centers sharing an edge.

function isPointerNearSectionPlaneHud(point: vec2 | undefined, pointerPos: vec2 | null) {
    if (!point || !pointerPos) {
        return false;
    }

    // Measure from the actual gizmo center (the anchor point + the SVG render offset), not the
    // raw anchor point - otherwise the hotspot sits above the gizmo and hovering its lower
    // half/ring edge misses, so the menu intermittently fails to appear.
    const dx = point[0] + GIZMO_CANVAS_OFFSET_X - pointerPos[0];
    const dy = point[1] + GIZMO_CANVAS_OFFSET_Y - pointerPos[1];

    return dx * dx + dy * dy <= MENU_VISIBILITY_SQR_DIST;
}

function shouldShowSectionPlaneMenuButton(params: {
    hasMouse: boolean;
    isMenuOpen: boolean;
    hasPendingMenuPoint: boolean;
    point: vec2 | undefined;
    pointerPos: vec2 | null;
}) {
    const { hasMouse, isMenuOpen, hasPendingMenuPoint, point, pointerPos } = params;
    return !hasMouse || isMenuOpen || hasPendingMenuPoint || isPointerNearSectionPlaneHud(point, pointerPos);
}

function setAnchorNodePosition(node: HTMLElement, point?: vec2) {
    const nextPoint = point ?? dummyPoint;
    node.style.setProperty("left", `${nextPoint[0]}px`);
    node.style.setProperty("top", `${nextPoint[1]}px`);
}

function hasAnchorMoved(node: HTMLElement, point: vec2) {
    const left = Number.parseFloat(node.style.left);
    const top = Number.parseFloat(node.style.top);

    if (!Number.isFinite(left) || !Number.isFinite(top)) {
        return true;
    }

    return Math.abs(left - point[0]) > POSITION_CHANGE_EPSILON || Math.abs(top - point[1]) > POSITION_CHANGE_EPSILON;
}

function setMenuButtonVisibility(node: HTMLElement, visible: boolean) {
    const menuButton = node.querySelector("[data-section-plane-menu-button]") as HTMLElement | null;
    if (!menuButton) {
        return;
    }

    menuButton.style.setProperty("opacity", visible ? "1" : "0");
    menuButton.style.setProperty("translate", visible ? "0" : "-20px");
    menuButton.style.setProperty("pointer-events", visible ? "auto" : "none");
}

/**
 * Applies (or clears) the gizmo drag feedback: grows the outer ring and brightens its fill
 * to a faint neon green. Driven by {@link useSectionPlaneDrag} on drag start/stop, since the
 * drag hook owns the gizmo DOM during a drag (updateAnchorElements is skipped while dragging).
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure DOM helper shared with useSectionPlaneDrag; no component, safe for fast refresh.
export function setGizmoDragFeedback(node: HTMLElement, active: boolean) {
    const ring = node.querySelector("[data-section-plane-ring]") as SVGGElement | null;
    if (ring) {
        ring.style.transform = `scale(${active ? GIZMO_HOVER_RING_SCALE : 1})`;
        ring.style.transformOrigin = `${GIZMO_CENTER_X}px ${GIZMO_CENTER_Y}px`;
    }

    const ringFill = node.querySelector("[data-section-plane-ring-fill]") as SVGEllipseElement | null;
    if (ringFill) {
        ringFill.setAttribute("fill", active ? SECTION_PLANE_NEON : "#000");
        ringFill.setAttribute("fill-opacity", active ? "0.15" : "0.3");
    }
}

/**
 * Per-plane animation state, keyed by anchor element id. Owned by a ref in
 * {@link SectionPlaneInteractions}
 */
type GizmoAnimState = {
    /** Ring scale (1.0 = rest, GIZMO_HOVER_RING_SCALE = hover/drag). */
    ringScale: Map<string, number>;
    /** Overall gizmo scale (1.0 = at-anchor, GIZMO_EDGE_SCALE = pushed to edge). */
    size: Map<string, number>;
};

function updateAnchorElements(params: {
    view: View;
    clipping: RenderState["clipping"];
    container: HTMLElement;
    openMenuIndex: number | null;
    closeMenu: () => void;
    lastPointerPos: vec2 | null;
    pendingCreateIndex: number | null;
    hasMouse: boolean;
    /** When provided, 2D gizmo positions are clamped to the safe area. */
    safeArea: ViewportSafeArea | null;
    canvasWidth: number;
    canvasHeight: number;
    anim: GizmoAnimState;
}) {
    const {
        view,
        clipping,
        container,
        openMenuIndex,
        closeMenu,
        lastPointerPos,
        pendingCreateIndex,
        hasMouse,
        safeArea,
        canvasWidth,
        canvasHeight,
        anim,
    } = params;
    const { ringScale: ringAnimScaleMap, size: gizmoSizeMap } = anim;
    const points = getSectionPlanePoints(view, clipping.planes, canvasWidth, canvasHeight);
    const offScreenIndices = new Set<number>();
    if (safeArea && canvasWidth > 0 && canvasHeight > 0) {
        for (const p of points) {
            if (p.point) {
                if (p.point[0] < 0 || p.point[0] > canvasWidth || p.point[1] < 0 || p.point[1] > canvasHeight) {
                    offScreenIndices.add(p.index);
                }
                const [cx, cy] = clampToSafeArea(p.point[0], p.point[1], canvasWidth, canvasHeight, safeArea);
                p.point[0] = cx;
                p.point[1] = cy;
            }
        }
    }

    const clamped = points.filter((p) => p.point);

    // Spread gizmos.
    const spanTop = safeArea?.top ?? 0;
    const spanBottom = canvasHeight - (safeArea?.bottom ?? 0);
    const spanLeft = safeArea?.left ?? 0;
    const spanRight = canvasWidth - (safeArea?.right ?? 0);

    // Left/right edge groups - distribute vertically.
    for (const edgeX of [spanLeft, spanRight]) {
        const group = clamped.filter((p) => p.point && Math.abs(p.point[0] - edgeX) < 2);
        group.sort((a, b) => a.point![1] - b.point![1]);
        const ys = distributeAlongAxis(
            group.map((p) => p.point![1]),
            GIZMO_SPACING_PX,
            spanTop,
            spanBottom,
        );
        group.forEach((p, i) => {
            p.point![1] = ys[i];
        });
    }

    // Top/bottom edge groups - distribute horizontally.
    for (const edgeY of [spanTop, spanBottom]) {
        const group = clamped.filter((p) => p.point && Math.abs(p.point[1] - edgeY) < 2);
        group.sort((a, b) => a.point![0] - b.point![0]);
        const xs = distributeAlongAxis(
            group.map((p) => p.point![0]),
            GIZMO_SPACING_PX,
            spanLeft,
            spanRight,
        );
        group.forEach((p, i) => {
            p.point![0] = xs[i];
        });
    }

    if (safeArea && canvasWidth > 0 && canvasHeight > 0) {
        for (const p of clamped) {
            const [cx, cy] = clampToSafeArea(p.point![0], p.point![1], canvasWidth, canvasHeight, safeArea);
            p.point![0] = cx;
            p.point![1] = cy;
        }
    }

    const pointByIndex = new Map(points.map(({ index, point }) => [index, point]));

    if (openMenuIndex !== null && !pointByIndex.get(openMenuIndex)) {
        closeMenu();
    }

    clipping.planes.forEach((plane, index) => {
        const node = container.querySelector(`#${getClippingPlaneAnchorId(index)}`) as HTMLElement | null;
        if (!node) {
            return;
        }

        const point = pointByIndex.get(index);
        if (!point) {
            if (pendingCreateIndex !== index) {
                setAnchorNodePosition(node, dummyPoint);
            }
            setMenuButtonVisibility(node, !hasMouse || openMenuIndex === index || pendingCreateIndex === index);
            ringAnimScaleMap.delete(getClippingPlaneAnchorId(index));
            gizmoSizeMap.delete(getClippingPlaneAnchorId(index));
            return;
        }

        if (openMenuIndex === index && hasAnchorMoved(node, point)) {
            closeMenu();
        }

        setAnchorNodePosition(node, point);
        // Hide the overflow menu only when the gizmo is genuinely stuck to a viewport edge and during drag.
        const isStuckToEdge = offScreenIndices.has(index);
        setMenuButtonVisibility(
            node,
            (openMenuIndex === index || !isStuckToEdge) &&
                shouldShowSectionPlaneMenuButton({
                    hasMouse,
                    isMenuOpen: openMenuIndex === index,
                    hasPendingMenuPoint: pendingCreateIndex === index,
                    point,
                    pointerPos: lastPointerPos,
                }),
        );

        // Compress along the ARROW direction in SVG space (GIZMO_NATIVE_ARROW_ANGLE)
        const disk = node.querySelector("[data-section-plane-disk]") as SVGGElement | null;
        if (disk && plane.normalOffset) {
            const normalWorld = vec3.normalize(
                vec3.create(),
                vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]),
            );
            const rotInv = quat.invert(quat.create(), view.renderState.camera.rotation as quat);
            const normalVS = vec3.transformQuat(vec3.create(), normalWorld, rotInv);
            const scaleX = Math.max(GIZMO_MIN_SQUASH, Math.abs(normalVS[2]));
            const a = GIZMO_NATIVE_ARROW_ANGLE;
            disk.setAttribute(
                "transform",
                `translate(${GIZMO_CENTER_X} ${GIZMO_CENTER_Y}) rotate(${a}) scale(${scaleX.toFixed(4)} 1) rotate(-${a}) translate(-${GIZMO_CENTER_X} -${GIZMO_CENTER_Y})`,
            );
        }

        // ── Ring hover scale animation ────────────────────────────────────────
        // Outer ring grows on hover. The drag-active grow is applied directly by
        // useSectionPlaneDrag (updateAnchorElements is skipped during a drag).
        const ring = node.querySelector("[data-section-plane-ring]") as SVGGElement | null;
        if (ring) {
            const anchorId = getClippingPlaneAnchorId(index);
            const prevScale = ringAnimScaleMap.get(anchorId) ?? 1.0;
            const gizmoCanvasX = point[0] + GIZMO_CANVAS_OFFSET_X;
            const gizmoCanvasY = point[1] + GIZMO_CANVAS_OFFSET_Y;
            let isHovered = false;
            if (lastPointerPos) {
                const dx = lastPointerPos[0] - gizmoCanvasX;
                const dy = lastPointerPos[1] - gizmoCanvasY;
                isHovered = dx * dx + dy * dy <= GIZMO_HOVER_RADIUS_PX * GIZMO_HOVER_RADIUS_PX;
            }
            const targetScale = isHovered ? GIZMO_HOVER_RING_SCALE : 1.0;
            const as = prevScale + (targetScale - prevScale) * GIZMO_RING_LERP;
            ringAnimScaleMap.set(anchorId, as);
            ring.style.transform = `scale(${as.toFixed(4)})`;
            ring.style.transformOrigin = `${GIZMO_CENTER_X}px ${GIZMO_CENTER_Y}px`;
        }

        // ── Gizmo overall size: shrink only when stuck to the edge as an off-screen indicator ──
        const arrow = node.querySelector("[data-section-plane-arrow]") as HTMLElement | null;
        if (arrow) {
            const anchorId = getClippingPlaneAnchorId(index);
            const prevSize = gizmoSizeMap.get(anchorId) ?? 1.0;
            const targetSize = isStuckToEdge ? GIZMO_EDGE_SCALE : 1.0;
            const newSize = prevSize + (targetSize - prevSize) * GIZMO_SIZE_LERP;
            gizmoSizeMap.set(anchorId, newSize);
            const offset = parseFloat(arrow.dataset.rotationOffset ?? "0");
            arrow.style.transform = `rotate(${getSectionPlaneArrowAngle(view, plane) + offset}deg) scale(${newSize.toFixed(4)})`;
        }
    });
}

/**
 * Section-plane gizmo icon.
 */
function SectionPlaneGizmoIcon() {
    const uid = useId();
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="55"
            height="60"
            fill="none"
            viewBox="0 0 110 120"
            style={{
                position: "absolute",
                left: "-15px",
                top: "-16px",
                transformOrigin: "30px 31px",
                display: "block",
                overflow: "visible",
            }}
            overflow="visible"
            data-section-plane-arrow
            data-rotation-offset={GIZMO_ROTATION_OFFSET}
        >
            <g opacity={0.7}>
                <mask
                    id={`${uid}a`}
                    width="50"
                    height="54"
                    x="16"
                    y="27"
                    maskUnits="userSpaceOnUse"
                    style={{ maskType: "alpha" }}
                >
                    <path fill="#e0e0e0" d="M65.389 42.476 55.57 80.134l-29.645-7.73-9.795-44.482z" />
                </mask>
                <g mask={`url(#${uid}a)`}>
                    <mask
                        id={`${uid}b`}
                        width="79.89"
                        height="46.984"
                        x="20.801"
                        y="38.421"
                        fill="#000"
                        maskUnits="userSpaceOnUse"
                    >
                        <path fill="#fff" d="M20.801 38.421h79.89v46.984h-79.89z" />
                        <path d="M47.767 45.662c1.484-.408 2.854.943 2.466 2.433L48.62 54.28a2 2 0 0 0 1.43 2.44l21.77 5.676a2 2 0 0 0 2.44-1.43l1.612-6.187c.389-1.49 2.244-2 3.34-.92L94.7 69.14c1.096 1.08.61 2.943-.874 3.351L72.85 78.263c-1.485.409-2.855-.943-2.466-2.432l1.613-6.187a2 2 0 0 0-1.431-2.44L48.797 61.53a2 2 0 0 0-2.44 1.43l-1.612 6.187c-.389 1.49-2.244 2-3.34.919l-15.487-15.28c-1.096-1.081-.61-2.943.874-3.352z" />
                    </mask>
                    <path
                        fill={SECTION_PLANE_ARROW_BACK}
                        d="M47.767 45.662c1.484-.408 2.854.943 2.466 2.433L48.62 54.28a2 2 0 0 0 1.43 2.44l21.77 5.676a2 2 0 0 0 2.44-1.43l1.612-6.187c.389-1.49 2.244-2 3.34-.92L94.7 69.14c1.096 1.08.61 2.943-.874 3.351L72.85 78.263c-1.485.409-2.855-.943-2.466-2.432l1.613-6.187a2 2 0 0 0-1.431-2.44L48.797 61.53a2 2 0 0 0-2.44 1.43l-1.612 6.187c-.389 1.49-2.244 2-3.34.919l-15.487-15.28c-1.096-1.081-.61-2.943.874-3.352z"
                    />
                    <path
                        fill={SECTION_PLANE_NEON}
                        d="m26.792 51.434.265.964zM48.797 61.53l-.252.967zm45.902 7.61-.703.712zm-.874 3.352.265.964zm-14.613-18.63-.702.711zm-31.445-8.199.265.964zm2.466 2.433-.968-.252-1.613 6.186.968.252.968.253 1.613-6.187zm-.182 8.626-.253.968 21.77 5.676.251-.968.253-.968-21.769-5.675zm24.208 4.245.968.252 1.613-6.186-.968-.252-.967-.253-1.613 6.187zm4.953-7.105-.702.711 15.486 15.279.703-.712.702-.712L79.914 53.15zm14.613 18.63-.266-.964L72.584 77.3l.266.964.265.965 20.975-5.773zm-23.441 3.34.967.252 1.613-6.187-.967-.252-.968-.252-1.613 6.186zm.182-8.627.252-.967L49.05 60.56l-.253.968-.252.967 21.769 5.676zM46.357 62.96l-.967-.252-1.613 6.187.968.252.967.252 1.613-6.186zm-4.952 7.106.702-.712L26.62 54.075l-.702.711-.702.712 15.486 15.279zm-14.613-18.63.265.963 20.975-5.772-.265-.964-.265-.964-20.975 5.772zm-.874 3.351.702-.712a1 1 0 0 1 .437-1.676l-.265-.964-.265-.964c-2.227.613-2.955 3.406-1.311 5.028zm18.827 14.36-.968-.252a1 1 0 0 1-1.67.46l-.702.711-.703.712c1.644 1.622 4.428.856 5.01-1.379zm4.052-7.617.253-.968a3 3 0 0 0-3.66 2.146l.967.252.968.253a1 1 0 0 1 1.22-.716zm23.2 8.115.967.252a3 3 0 0 0-2.146-3.66l-.252.968-.252.968a1 1 0 0 1 .715 1.22zm.853 8.62-.266-.965a1 1 0 0 1-1.233-1.216l-.967-.252-.968-.253c-.582 2.235 1.472 4.262 3.7 3.65zm21.849-9.125-.703.712a1 1 0 0 1-.436 1.676l.265.964.265.964c2.227-.612 2.955-3.406 1.311-5.028zM75.872 54.78l.968.253a1 1 0 0 1 1.67-.46l.702-.712.702-.711c-1.644-1.622-4.427-.856-5.01 1.378zm-4.053 7.618-.252.968a3 3 0 0 0 3.66-2.147l-.968-.252-.967-.252a1 1 0 0 1-1.22.715zM48.62 54.28l-.968-.252a3 3 0 0 0 2.146 3.66l.253-.968.252-.967a1 1 0 0 1-.715-1.22zm1.613-6.186.968.252c.582-2.235-1.473-4.262-3.7-3.65l.266.965.265.964a1 1 0 0 1 1.233 1.217z"
                        mask={`url(#${uid}b)`}
                    />
                </g>
            </g>

            <g data-section-plane-disk>
                <g data-section-plane-ring>
                    <ellipse
                        data-section-plane-ring-fill
                        cx={GIZMO_CENTER_X}
                        cy={GIZMO_CENTER_Y}
                        rx={GIZMO_RING_RADIUS}
                        ry={GIZMO_RING_RADIUS}
                        fill="#000"
                        fillOpacity={0.3}
                        stroke={SECTION_PLANE_NEON}
                        strokeWidth={2}
                    />
                </g>
                <circle
                    data-section-plane-dot
                    cx={GIZMO_CENTER_X}
                    cy={GIZMO_CENTER_Y}
                    r={GIZMO_HUB_RADIUS}
                    fill={SECTION_PLANE_NEON}
                />
            </g>

            <mask
                id={`${uid}c`}
                width="49"
                height="49"
                x="55"
                y="41"
                maskUnits="userSpaceOnUse"
                style={{ maskType: "alpha" }}
            >
                <path fill="#e0e0e0" d="M103.882 49.09 92.427 89.744l-36.648-10.41 9.818-37.658z" />
            </mask>
            <g mask={`url(#${uid}c)`}>
                <mask
                    id={`${uid}d`}
                    width="79.89"
                    height="46.984"
                    x="20.8"
                    y="38.421"
                    fill="#000"
                    maskUnits="userSpaceOnUse"
                >
                    <path fill="#fff" d="M20.8 38.421h79.89v46.984H20.8z" />
                    <path d="M47.766 45.662c1.484-.408 2.854.943 2.466 2.433l-1.613 6.186a2 2 0 0 0 1.43 2.44l21.77 5.676a2 2 0 0 0 2.44-1.43l1.612-6.187c.389-1.49 2.244-2 3.34-.92l15.487 15.28c1.096 1.08.61 2.943-.874 3.351l-20.975 5.772c-1.485.409-2.855-.943-2.466-2.432l1.613-6.187a2 2 0 0 0-1.431-2.44L48.796 61.53a2 2 0 0 0-2.44 1.43l-1.613 6.187c-.388 1.49-2.243 2-3.34.919l-15.485-15.28c-1.096-1.081-.61-2.943.874-3.352z" />
                </mask>
                <path
                    fill={SECTION_PLANE_DARK_GREEN}
                    d="M47.766 45.662c1.484-.408 2.854.943 2.466 2.433l-1.613 6.186a2 2 0 0 0 1.43 2.44l21.77 5.676a2 2 0 0 0 2.44-1.43l1.612-6.187c.389-1.49 2.244-2 3.34-.92l15.487 15.28c1.096 1.08.61 2.943-.874 3.351l-20.975 5.772c-1.485.409-2.855-.943-2.466-2.432l1.613-6.187a2 2 0 0 0-1.431-2.44L48.796 61.53a2 2 0 0 0-2.44 1.43l-1.613 6.187c-.388 1.49-2.243 2-3.34.919l-15.485-15.28c-1.096-1.081-.61-2.943.874-3.352z"
                />
                <path
                    fill={SECTION_PLANE_NEON}
                    d="m26.79 51.434.266.964zM48.797 61.53l-.252.967zm45.902 7.61-.702.712zm-.874 3.352.265.964zM79.21 53.861l-.702.711zm-31.445-8.199.265.964zm2.466 2.433-.968-.252-1.613 6.186.968.252.968.253 1.613-6.187zm-.182 8.626-.252.968 21.768 5.676.252-.968.253-.968-21.769-5.675zm24.208 4.245.968.252 1.613-6.186-.968-.252-.967-.253-1.613 6.187zm4.953-7.105-.702.711 15.487 15.279.702-.712.702-.712L79.913 53.15zm14.613 18.63-.266-.964-20.975 5.772.266.964.265.965 20.975-5.773zm-23.441 3.34.967.252 1.613-6.187-.967-.252-.968-.252-1.613 6.186zm.182-8.627.252-.967L49.05 60.56l-.253.968-.252.967 21.769 5.676zM46.357 62.96l-.968-.252-1.613 6.187.967.252.968.252 1.613-6.186zm-4.953 7.106.702-.712-15.487-15.278-.702.711-.702.712L40.7 70.777zM26.79 51.435l.265.963 20.975-5.772-.265-.964-.265-.964-20.975 5.772zm-.874 3.351.702-.712a1 1 0 0 1 .437-1.676l-.265-.964-.265-.964c-2.227.613-2.955 3.406-1.311 5.028zm18.826 14.36-.967-.252a1 1 0 0 1-1.67.46l-.702.711-.703.712c1.644 1.622 4.428.856 5.01-1.379zm4.053-7.617.253-.968a3 3 0 0 0-3.66 2.146l.967.252.968.253a1 1 0 0 1 1.22-.716zm23.2 8.115.967.252a3 3 0 0 0-2.146-3.66l-.252.968-.252.968a1 1 0 0 1 .715 1.22zm.853 8.62-.266-.965a1 1 0 0 1-1.233-1.216l-.967-.252-.968-.253c-.583 2.235 1.472 4.262 3.7 3.65zm21.849-9.125-.702.712a1 1 0 0 1-.437 1.676l.265.964.265.964c2.227-.612 2.955-3.406 1.311-5.028zM75.87 54.78l.968.253a1 1 0 0 1 1.67-.46l.702-.712.703-.711c-1.645-1.622-4.428-.856-5.01 1.378zm-4.053 7.618-.252.968a3 3 0 0 0 3.66-2.147l-.968-.252-.967-.252a1 1 0 0 1-1.22.715zM48.62 54.28l-.968-.252a3 3 0 0 0 2.146 3.66l.253-.968.252-.967a1 1 0 0 1-.715-1.22zm1.613-6.186.968.252c.582-2.235-1.473-4.262-3.7-3.65l.266.965.265.964a1 1 0 0 1 1.233 1.217z"
                    mask={`url(#${uid}d)`}
                />
            </g>
        </svg>
    );
}

/**
 * Renders interactive anchors for section planes.
 * Each anchor can be dragged to move the plane and has a context menu for plane operations.
 */
export const SectionPlaneInteractions = forwardRef(function SectionPlaneInteractions(_props, ref) {
    const {
        state: { view, canvas },
    } = useExplorerGlobals(false);
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const clipping = useAppSelector(selectClippingPlanes);
    const clippingRef = useRef(clipping);
    clippingRef.current = clipping;
    const actions = useClippingPlaneActions();
    const undoRedo = useSectionPlaneUndoRedo();
    const onWheel = useRedirectWheelEvents();
    const hasMouse = hasMouseSupport();

    // Stores the current displayed 3D anchor position (used during drags for off-screen/edge-clamped gizmos).
    const displayPosRefEarly = useRef<vec3 | null>(null);
    // True only while the active plane's anchor is off-screen and the gizmo is clamped to the edge.
    const isEdgeClampedRef = useRef(false);

    const getDisplayAnchorPos = useCallback((index: number): vec3 | null => {
        const activePlaneIndex = getActiveSectionPlaneIndex(clippingRef.current.planes);
        if (index !== activePlaneIndex || !isEdgeClampedRef.current) {
            return null;
        }
        return displayPosRefEarly.current;
    }, []);

    const { movingPlaneIndex, handlePointerDown } = useSectionPlaneDrag({
        view,
        clipping,
        actions,
        undoRedo,
        getDisplayAnchorPos,
        onTapPlane: (index, screenPoint) => {
            clearSectionPlaneCreateOrigin();
            showSectionPlaneMenuButtonAtPoint(index, screenPoint);
            if (view) {
                const plane = clippingRef.current.planes[index];
                const anchor = plane?.anchorPos;
                if (anchor) {
                    const camera = view.renderState.camera;
                    const cameraDir = getCameraDir(camera.rotation);
                    const dist = Math.max(1, vec3.dist(camera.position, anchor));
                    const flyPos = vec3.scaleAndAdd(vec3.create(), anchor, cameraDir, -dist);
                    dispatch(
                        renderActions.setCamera({
                            type: camera.kind === "orthographic" ? CameraType.Orthographic : CameraType.Pinhole,
                            goTo: {
                                position: flyPos,
                                rotation: camera.rotation,
                            },
                        }),
                    );
                    displayPosRefEarly.current = null;
                }
            }
        },
        onDragStateChange: (index, dragging) => {
            const anchorId = getClippingPlaneAnchorId(index);
            const node = containerRef.current?.querySelector(`#${anchorId}`) as HTMLElement | null;
            if (!node) {
                return;
            }
            setGizmoDragFeedback(node, dragging);
            // Keep the ring-scale anim map in sync with the direct DOM write so the
            // next updateAnchorElements pass does not jump back to a stale scale.
            gizmoAnimRef.current.ringScale.set(anchorId, dragging ? GIZMO_HOVER_RING_SCALE : 1);
        },
    });
    const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
    const openMenuIndexRef = useRef<number | null>(openMenuIndex);
    openMenuIndexRef.current = openMenuIndex;
    const prevClippingRef = useRef(clipping);
    const lastPointerPosRef = useRef<vec2 | null>(null);
    const gizmoAnimRef = useRef<GizmoAnimState>({ ringScale: new Map(), size: new Map() });
    useEffect(
        () => () => {
            gizmoAnimRef.current.ringScale.clear();
            gizmoAnimRef.current.size.clear();
        },
        [],
    );
    const pendingCreateOrigin = useSectionPlaneCreateOrigin();
    const pendingCreateIndex = pendingCreateOrigin?.index ?? null;
    const pendingCreateIndexRef = useRef(pendingCreateIndex);
    pendingCreateIndexRef.current = pendingCreateIndex;

    const picker = useAppSelector(selectPicker);
    const isHudVisible = picker === Picker.SectionPlane;

    // ── Recenter on mode (re)entry ────────────────────────────────────
    useEffect(() => {
        if (!isHudVisible || !view || pendingCreateIndexRef.current !== null) {
            return;
        }
        const planes = clippingRef.current.planes;
        const index = getActiveSectionPlaneIndex(planes);
        const active = planes[index];
        if (!active?.anchorPos) {
            return;
        }
        const target = getSectionPlaneViewCenterAnchor(active, view);
        if (!target) {
            return;
        }
        dispatch(
            renderActions.setClippingPlanes({
                planes: planes.map((plane, i) => (i === index ? { ...plane, anchorPos: target } : plane)),
            }),
        );
    }, [isHudVisible, view, dispatch]);

    const safeArea = useViewportSafeArea();

    // ── Gizmo display-position tracking ─────────────────────────────────────────
    const displayPosRef = displayPosRefEarly;

    // Reset the display position so the gizmo snaps to the canonical anchor
    // whenever the HUD is deactivated or a plane drag ends.
    useLayoutEffect(() => {
        if (!isHudVisible) {
            displayPosRef.current = null;
        }
    }, [displayPosRef, isHudVisible]);

    useLayoutEffect(() => {
        if (movingPlaneIndex === -1) {
            displayPosRef.current = null;
        }
    }, [displayPosRef, movingPlaneIndex]);

    // Reset on plane count change so the
    // gizmo snaps to the new anchor instead of lerping from a stale position.
    useLayoutEffect(() => {
        displayPosRef.current = null;
    }, [clipping.planes.length, displayPosRef]);

    const runAnchorUpdate = useCallback(
        (lastPointerPos: vec2 | null = lastPointerPosRef.current) => {
            if (!view || !containerRef.current || movingPlaneIndex !== -1) {
                return;
            }

            let clipping = clippingRef.current;

            if (isHudVisible && pendingCreateIndex === null && canvas) {
                const activePlaneIndex = getActiveSectionPlaneIndex(clipping.planes);
                const activePlane = clipping.planes[activePlaneIndex];

                if (activePlane?.anchorPos) {
                    // Centroid
                    const centroid = activePlane.anchorPos;

                    const computedTarget = computeSectionPlaneGizmoTarget(
                        activePlane,
                        view,
                        canvas.clientWidth,
                        canvas.clientHeight,
                        safeArea,
                    );
                    isEdgeClampedRef.current = computedTarget !== null;
                    const target = computedTarget ?? centroid;

                    // Lerp the displayed position toward the target so transitions are
                    // smooth whether sliding to the edge or returning to the original position.
                    const prev = displayPosRef.current;
                    const displayPos = prev
                        ? vec3.lerp(vec3.create(), prev, target, GIZMO_DISPLAY_POS_LERP)
                        : vec3.clone(target);
                    displayPosRef.current = displayPos;

                    // Inject the display position into a local clipping snapshot.
                    if (!vec3.exactEquals(displayPos, centroid)) {
                        const displayPlanes = clipping.planes.map((plane, idx) =>
                            idx === activePlaneIndex ? { ...plane, anchorPos: displayPos } : plane,
                        ) as typeof clipping.planes;
                        clipping = { ...clipping, planes: displayPlanes };
                    }
                }
            }

            updateAnchorElements({
                view,
                clipping,
                container: containerRef.current,
                openMenuIndex: openMenuIndexRef.current,
                closeMenu: () => setOpenMenuIndex(null),
                lastPointerPos,
                pendingCreateIndex,
                hasMouse,
                safeArea: canvas ? safeArea : null,
                canvasWidth: canvas?.clientWidth ?? 0,
                canvasHeight: canvas?.clientHeight ?? 0,
                anim: gizmoAnimRef.current,
            });
        },
        [canvas, displayPosRef, hasMouse, isHudVisible, movingPlaneIndex, pendingCreateIndex, safeArea, view],
    );

    // Always points to the latest runAnchorUpdate so pointer handlers never go stale.
    const runAnchorUpdateRef = useRef(runAnchorUpdate);
    runAnchorUpdateRef.current = runAnchorUpdate;

    const updatePointerPosition = useCallback(
        (clientX: number, clientY: number) => {
            if (!canvas || !hasMouse) {
                return;
            }

            const canvasRect = canvas.getBoundingClientRect();
            const nextPos = vec2.fromValues(clientX - canvasRect.left, clientY - canvasRect.top);
            clearSectionPlaneCreateOrigin();
            lastPointerPosRef.current = nextPos;
            runAnchorUpdateRef.current(nextPos);
        },
        [canvas, hasMouse],
    );

    useImperativeHandle(ref, () => ({ update: runAnchorUpdate }), [runAnchorUpdate]);

    useEffect(() => {
        if (!canvas || !hasMouse) {
            return;
        }

        const updateFromPointerEvent = (event: Event) => {
            if (!(event instanceof PointerEvent)) {
                return;
            }
            updatePointerPosition(event.clientX, event.clientY);
        };

        canvas.addEventListener("pointermove", updateFromPointerEvent);
        canvas.addEventListener("pointerdown", updateFromPointerEvent);

        return () => {
            canvas.removeEventListener("pointermove", updateFromPointerEvent);
            canvas.removeEventListener("pointerdown", updateFromPointerEvent);
        };
    }, [canvas, hasMouse, updatePointerPosition]);

    useEffect(() => {
        if (openMenuIndex !== null && openMenuIndex >= clipping.planes.length) {
            setOpenMenuIndex(null);
        }
        if (pendingCreateOrigin && pendingCreateOrigin.index >= clipping.planes.length) {
            clearSectionPlaneCreateOrigin();
        }
    }, [clipping.planes.length, openMenuIndex, pendingCreateOrigin]);

    useEffect(() => {
        const planesOrEnabledChanged =
            prevClippingRef.current.enabled !== clipping.enabled || prevClippingRef.current.planes !== clipping.planes;
        const clippingChanged = prevClippingRef.current !== clipping && planesOrEnabledChanged;

        if (openMenuIndex !== null && clippingChanged) {
            setOpenMenuIndex(null);
        }
        if (pendingCreateOrigin && planesOrEnabledChanged) {
            const targetPlane = clipping.planes[pendingCreateOrigin.index];
            if (!targetPlane?.anchorPos) {
                clearSectionPlaneCreateOrigin();
            }
        }
        prevClippingRef.current = clipping;
    }, [clipping, openMenuIndex, pendingCreateOrigin]);

    useLayoutEffect(() => {
        runAnchorUpdate();
    }, [clipping, clipping.planes.length, openMenuIndex, runAnchorUpdate]);

    const sectionPlaneLimitReached = useAppSelector(selectSectionPlaneLimitReached);

    useEffect(() => {
        if (!sectionPlaneLimitReached) {
            return;
        }
        const t = setTimeout(() => {
            dispatch(renderActions.setSectionPlaneLimitReached(false));
        }, 5000);
        return () => clearTimeout(t);
    }, [dispatch, sectionPlaneLimitReached]);

    if (!view) {
        return null;
    }

    const sectionPlanePoints = getSectionPlanePoints(
        view,
        clipping.planes,
        canvas?.clientWidth ?? 0,
        canvas?.clientHeight ?? 0,
    );
    const pointByIndex = new Map(sectionPlanePoints.map(({ index, point }) => [index, point]));

    return (
        <>
            <Box ref={containerRef} style={{ display: isHudVisible ? "block" : "none" }}>
                {clipping.planes.map((plane, index) => (
                    <SectionPlaneBox
                        key={index}
                        index={index}
                        plane={plane}
                        view={view}
                        movingPlaneIndex={movingPlaneIndex}
                        handlePointerDown={handlePointerDown}
                        point={pointByIndex.get(index)}
                        lastPointerPos={lastPointerPosRef.current}
                        pendingMenuPoint={pendingCreateOrigin?.index === index ? pendingCreateOrigin.point : null}
                        onWheel={onWheel}
                        isMenuOpen={openMenuIndex === index}
                        onOpenMenu={() => setOpenMenuIndex(index)}
                        onCloseMenu={() => setOpenMenuIndex((current) => (current === index ? null : current))}
                        onTrackPointer={updatePointerPosition}
                        hasMouse={hasMouse}
                    />
                ))}
            </Box>
            {sectionPlaneLimitReached && <SectionPlaneLimitToast />}
        </>
    );
});

function SectionPlaneBox({
    index,
    movingPlaneIndex,
    handlePointerDown,
    point,
    lastPointerPos,
    pendingMenuPoint,
    onWheel,
    isMenuOpen,
    onOpenMenu,
    onCloseMenu,
    onTrackPointer,
    hasMouse,
}: {
    index: number;
    plane: RenderState["clipping"]["planes"][number];
    view: View;
    movingPlaneIndex: number;
    handlePointerDown: (e: React.PointerEvent, index: number) => void;
    point: vec2 | undefined;
    lastPointerPos: vec2 | null;
    pendingMenuPoint: vec2 | null;
    onWheel: (evt: React.WheelEvent) => void;
    isMenuOpen: boolean;
    onOpenMenu: () => void;
    onCloseMenu: () => void;
    onTrackPointer: (clientX: number, clientY: number) => void;
    hasMouse: boolean;
}) {
    const theme = useTheme();
    const anchorBoxRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!anchorBoxRef.current) {
            return;
        }

        setAnchorNodePosition(anchorBoxRef.current, dummyPoint);
    }, []);

    useLayoutEffect(() => {
        if (!anchorBoxRef.current || !pendingMenuPoint) {
            return;
        }

        setAnchorNodePosition(anchorBoxRef.current, pendingMenuPoint);
    }, [pendingMenuPoint]);

    const isCloseHudVisible =
        movingPlaneIndex === -1 &&
        shouldShowSectionPlaneMenuButton({
            hasMouse,
            isMenuOpen,
            hasPendingMenuPoint: Boolean(pendingMenuPoint),
            point,
            pointerPos: lastPointerPos,
        });

    return (
        <Box
            ref={anchorBoxRef}
            sx={{
                position: "absolute",
                translate: "calc(-15px) calc(-15px)",
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                opacity: movingPlaneIndex === -1 || movingPlaneIndex === index ? 1 : 0,
                pointerEvents: movingPlaneIndex === -1 || movingPlaneIndex === index ? "auto" : "none",
                transition: "opacity 0.1s",
            }}
            id={getClippingPlaneAnchorId(index)}
            onWheel={onWheel}
            onPointerEnter={
                hasMouse ? (event: React.PointerEvent) => onTrackPointer(event.clientX, event.clientY) : undefined
            }
            onPointerMove={
                hasMouse ? (event: React.PointerEvent) => onTrackPointer(event.clientX, event.clientY) : undefined
            }
        >
            <SectionPlaneMoveButton
                size="small"
                onPointerDown={(e) => handlePointerDown(e, index)}
                aria-label="Move section plane"
            >
                <SectionPlaneGizmoIcon />
            </SectionPlaneMoveButton>
            <SectionPlaneMenu
                index={index}
                visible={isCloseHudVisible}
                anchorEl={anchorBoxRef.current}
                isOpen={isMenuOpen}
                onOpen={() => {
                    clearSectionPlaneCreateOrigin();
                    onOpenMenu();
                }}
                onClose={() => {
                    clearSectionPlaneCreateOrigin();
                    onCloseMenu();
                }}
            />
            <Box
                data-offset
                sx={{
                    position: "absolute",
                    left: 0,
                    top: -40,
                    background: theme.palette.background.paper,
                    px: 0.5,
                    borderRadius: 1,
                    fontSize: "small",
                    color: "grey",
                    display: "none",
                    whiteSpace: "nowrap",
                    translate: "calc(15px - 50%)",
                }}
            >
                Offset
            </Box>
        </Box>
    );
}

function SectionPlaneMenu({
    index,
    visible,
    anchorEl,
    isOpen,
    onOpen,
    onClose,
}: {
    index: number;
    visible: boolean;
    anchorEl: HTMLElement | null;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { flipClippingPlaneWithUndo, deleteClippingPlaneWithUndo } = useSectionPlaneUndoRedo();

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onOpen();
    };

    const handleFlipDirection = () => {
        flipClippingPlaneWithUndo(index);
        onClose();
    };

    const handleDelete = () => {
        deleteClippingPlaneWithUndo(index);
        onClose();
    };

    visible = visible || isOpen;

    return (
        <>
            <StyledMenuButton
                size="small"
                onClick={openMenu}
                aria-label="Section plane menu"
                data-section-plane-menu-button
                sx={{
                    opacity: visible ? 1 : 0,
                    transition: "opacity 0.1s, translate 0.1s",
                    translate: visible ? 0 : "-20px",
                    pointerEvents: visible ? "auto" : "none",
                }}
            >
                <MoreVert fontSize="inherit" />
            </StyledMenuButton>

            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={onClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={handleFlipDirection}>
                    <ListItemIcon>
                        <FlipCameraAndroid fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("flipDirection")}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                        <Delete fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("delete")}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

const SectionPlaneMoveButton = styled(IconButton)<IconButtonProps>(
    () => css`
        background-color: transparent;
        opacity: 0.9;
        transition: opacity 0.2s;
        touch-action: none;
        width: 30px;
        height: 30px;
        overflow: visible;
        padding: 0;
        cursor: grab;
        &:active {
            cursor: grabbing;
        }
        &:hover {
            background-color: transparent;
            opacity: 1;
        }
    `,
);

const StyledMenuButton = styled(IconButton)(
    ({ theme }) => css`
        background-color: ${theme.palette.background.paper};
        color: ${theme.palette.text.primary};
        &:hover {
            background-color: ${theme.palette.grey["100"]};
        }
    `,
);

/**
 * Error toast shown when the section-plane limit is reached.
 */
function SectionPlaneLimitToast() {
    const MAX_SECTION_PLANES = 6;
    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 99999,
                pointerEvents: "none",
            }}
        >
            <Toast variant="error">
                Sorry, the section plane couldn't be created because the maximum limit of {MAX_SECTION_PLANES} has been
                reached.
            </Toast>
        </Box>
    );
}
